import React, { useCallback, useEffect, useRef, useState } from "react";
import { ScanLine, ShieldCheck, Clock, Ban, Database, ListChecks, ArrowRight, Zap, Radio, Camera, Pause, Play } from "lucide-react";
import { api } from "../../services/api";

const BANDS = [
  {
    id: "green", key: "PRE-VALIDATED", icon: ShieldCheck, color: "#22c55e",
    label: "PRE-VALIDATED", cond: null, action: "Proceed with normal refueling.",
  },
  {
    id: "yellow", key: "UPCOMING RISK", icon: Clock, color: "#eab308",
    label: "HYDRO-TEST EXPIRING SOON", cond: "Approaching expiry", action: "Follow IGL check / alert process.",
  },
  {
    id: "red", key: "STOP", icon: Ban, color: "#ef4444",
    label: "STOP — VERIFICATION REQUIRED", cond: "Hydro-test expired · unauthorized / unverified cylinder",
    action: "Do not refuel until approved verification.",
  },
];

// Map a computed state to one of the three operator colour bands
const STATE_BAND = {
  "PRE-VALIDATED": "green",
  "UPCOMING RISK": "yellow",
  "VERIFICATION REQUIRED": "red",
  "COMPLIANCE FLAG": "red",
};

const SAMPLES = ["DL01AB1234", "HR55LM2468", "DL01CD5678", "UP16GH3456"];
const DOT = { green: "#22c55e", yellow: "#eab308", red: "#ef4444" };

export default function RVOperator() {
  const [plate, setPlate] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(true);
  const [recent, setRecent] = useState([]);
  const timer = useRef(null);

  const apply = useCallback((r) => {
    setResult(r);
    setPlate(r.plate || "");
    setRecent((prev) => {
      const band = r.found ? STATE_BAND[r.state] : "red";
      const row = { scan_id: r.scan_id, plate: r.plate, band, time: r.captured_at, state: r.state };
      return [row, ...prev.filter((x) => x.scan_id !== r.scan_id)].slice(0, 6);
    });
  }, []);

  // Manual scan (pauses the live feed so the operator can inspect)
  const scan = async (p) => {
    const q = (p || plate).trim().toUpperCase();
    if (!q) return;
    setLive(false);
    setPlate(q);
    setBusy(true);
    setResult(null);
    const r = await api.rvLookup(q);
    setTimeout(() => { setResult(r); setBusy(false); }, 350);
  };

  // Live ANPR capture feed: poll the lane camera every ~3.5s
  useEffect(() => {
    if (!live) {
      if (timer.current) clearInterval(timer.current);
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.rvScanNext();
        if (!cancelled) apply(r);
      } catch (e) { /* ignore transient */ }
    };
    tick();
    timer.current = setInterval(tick, 3500);
    return () => { cancelled = true; clearInterval(timer.current); };
  }, [live, apply]);

  const activeBand = result ? (result.found ? STATE_BAND[result.state] : "red") : null;

  return (
    <div className="rv-op" data-testid="rv-operator-board">
      <div className="rv-op-topline">
        <div>
          <h1 className="rv-op-title">Know the Refuel Status at a Glance</h1>
          <p className="rv-op-sub">Live ANPR capture feed — the board updates as each vehicle is scanned at the lane.</p>
        </div>
        <button
          className={`rv-op-live ${live ? "on" : "off"}`}
          onClick={() => setLive((v) => !v)}
          data-testid="rv-op-live-toggle"
        >
          {live ? <Radio size={16} /> : <Camera size={16} />}
          <span>{live ? "LIVE · Lane Camera 01" : "Feed paused"}</span>
          {live ? <Pause size={14} /> : <Play size={14} />}
        </button>
      </div>

      {/* Scan control (manual override) */}
      <div className="rv-op-scan">
        <div className="rv-op-scanbox">
          <ScanLine size={20} />
          <input
            value={plate}
            onChange={(e) => setPlate(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && scan()}
            placeholder="Scan or type plate (e.g. DL01CD5678)"
            data-testid="rv-op-plate-input"
          />
          <button onClick={() => scan()} disabled={busy} data-testid="rv-op-scan-btn">
            <Zap size={15} /> {busy ? "Checking…" : "Scan"}
          </button>
        </div>
        <div className="rv-op-samples">
          {SAMPLES.map((s) => (
            <button key={s} className="rv-op-sample" onClick={() => scan(s)} data-testid={`rv-op-sample-${s}`}>{s}</button>
          ))}
        </div>
      </div>

      {/* Recent captures ticker */}
      {recent.length > 0 && (
        <div className="rv-op-ticker" data-testid="rv-op-ticker">
          <span className="rv-op-ticker-lbl"><Camera size={13} /> Recent captures</span>
          {recent.map((c) => (
            <span key={c.scan_id} className={`rv-op-cap ${c.band} ${c.plate === result?.plate ? "now" : ""}`}
              style={{ "--d": DOT[c.band] }} data-testid={`rv-op-cap-${c.scan_id}`}>
              <i className="rv-op-cap-dot" /> {c.plate} <em>{c.time}</em>
            </span>
          ))}
        </div>
      )}

      {/* Flow chevrons */}
      <div className="rv-op-flow">
        <div className="rv-op-chev">
          <div className="rv-op-plate-tag">{result?.plate || plate || "—"}</div>
          <span>{result?.scan_id ? `Captured ${result.captured_at}` : "Scan plate"}</span>
        </div>
        <ArrowRight size={22} className="rv-op-arrow" />
        <div className="rv-op-chev"><Database size={22} /><span>DotIQ checks vehicle + CNG records</span></div>
        <ArrowRight size={22} className="rv-op-arrow" />
        <div className="rv-op-chev"><ListChecks size={22} /><span>Follow the status band</span></div>
      </div>

      {/* Colour bands */}
      <div className="rv-op-bands">
        {BANDS.map((b) => {
          const Icon = b.icon;
          const active = activeBand === b.id;
          const dim = activeBand && !active;
          return (
            <div
              key={b.id}
              className={`rv-op-band ${b.id} ${active ? "active" : ""} ${dim ? "dim" : ""}`}
              style={{ "--band": b.color }}
              data-testid={`rv-op-band-${b.id}`}
            >
              <div className="rv-op-band-ic"><Icon size={34} /></div>
              <div className="rv-op-band-main">
                <div className="rv-op-band-label">{b.label}</div>
                {b.cond && <div className="rv-op-band-cond">{b.cond}</div>}
                {active && result && (
                  <div className="rv-op-band-live">
                    {result.found ? `${result.plate} — ${result.reason}` : `${result.plate} — no pre-computed state`}
                  </div>
                )}
              </div>
              <div className="rv-op-band-action">{b.action}</div>
              {active && <div className="rv-op-band-flag">◀ {result?.plate}</div>}
            </div>
          );
        })}
      </div>

      <footer className="rv-op-foot">
        <ShieldCheck size={15} /> Final refueling and exception decisions remain with IGL procedures and authorized personnel.
      </footer>
    </div>
  );
}
